import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Play, Square, RotateCcw, Sparkles, Target, Timer, ChevronDown, Hourglass } from "lucide-react";
import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import HourglassView from "./HourglassView";

/**
 * TimeMasterCard — matches the ModuleCard family visual language:
 *  • rounded-[28px], 4px white border, layered premium shadow
 *  • soft glow behind icon + dot-grid pattern in bottom-right (AI vibe)
 *  • glass-morphism icon tile with corner sparkle
 *  • refined dropdowns (custom chevron), typography tightening,
 *    circular-progress ideal-vs-elapsed ring, elegant Start/Stop pills
 */

// Verbatim port of the user's Python `calculate_ideal_time`. Returns minutes
// (float, 1-decimal precision).
function calculateIdealMinutes(classNo, subjectCategory, marks) {
  const BASE_TIME = 2.0;
  let classFactor;
  if (classNo <= 2) classFactor = 1.20;
  else if (classNo <= 5) classFactor = 1.10;
  else if (classNo <= 8) classFactor = 1.00;
  else if (classNo <= 10) classFactor = 0.95;
  else classFactor = 1.00;
  const CATEGORY = {
    NUMERICAL: 0.90,
    SCIENCE: 1.00,
    TECHNICAL: 0.95,
    SOCIAL: 1.05,
    LANGUAGE: 1.10,
    OTHER: 1.00,
  };
  const subjectFactor = CATEGORY[(subjectCategory || "").toUpperCase()] ?? 1.00;
  const mins = BASE_TIME * marks * classFactor * subjectFactor;
  return Math.round(mins * 10) / 10; // 1-decimal
}

function fmtMMSS(totalSec) {
  const s = Math.max(0, Math.floor(totalSec));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

const CLASSES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MARKS = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20];
const SUBJECTS = [
  { value: "NUMERICAL", labelKey: "time_master_subj_numerical" },
  { value: "SCIENCE",   labelKey: "time_master_subj_science" },
  { value: "TECHNICAL", labelKey: "time_master_subj_technical" },
  { value: "SOCIAL",    labelKey: "time_master_subj_social" },
  { value: "LANGUAGE",  labelKey: "time_master_subj_language" },
  { value: "OTHER",     labelKey: "time_master_subj_other" },
];

/* -------- small pieces ------------------------------------------------- */

function FieldSelect({ label, value, onChange, children, testId }) {
  return (
    <label className="relative flex flex-col gap-1">
      <span className="text-[10px] font-heading font-bold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </span>
      <div className="relative">
        <select
          data-testid={testId}
          value={value}
          onChange={onChange}
          className="w-full appearance-none bg-white/95 border-2 border-white rounded-2xl pl-3 pr-9 py-2.5 font-heading font-bold text-slate-800 ring-1 ring-slate-900/5 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_4px_10px_-4px_rgba(15,23,42,0.15)] focus:outline-none focus:ring-2 focus:ring-indigo-300"
        >
          {children}
        </select>
        <ChevronDown
          size={16}
          strokeWidth={2.75}
          className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500"
        />
      </div>
    </label>
  );
}

/* -------- main card ---------------------------------------------------- */

export default function TimeMasterCard() {
  const { lang } = useAuth();
  const [classNo, setClassNo] = useState("");
  const [subject, setSubject] = useState("");
  const [marks, setMarks] = useState("");
  const [running, setRunning] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [hourglassOpen, setHourglassOpen] = useState(false);
  const startedAtRef = useRef(null);
  const rafRef = useRef(null);
  // Live elapsed seconds — kept in a ref so <HourglassView> can read the
  // current value each animation frame without forcing React re-renders.
  const elapsedRef = useRef(0);

  const allChosen = classNo !== "" && subject !== "" && marks !== "";
  const idealMinutes = useMemo(
    () => (allChosen ? calculateIdealMinutes(Number(classNo), subject, Number(marks)) : null),
    [allChosen, classNo, subject, marks]
  );
  const idealSec = idealMinutes != null ? Math.round(idealMinutes * 60) : null;

  // Elapsed timer via rAF for smooth per-second updates (no drift).
  useEffect(() => {
    if (!running) return undefined;
    const tick = () => {
      const now = performance.now();
      const el = (now - startedAtRef.current) / 1000;
      elapsedRef.current = el;
      setElapsedSec(el);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [running]);

  const onStart = () => {
    startedAtRef.current = performance.now();
    elapsedRef.current = 0;
    setElapsedSec(0);
    setRunning(true);
  };
  const onStop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (startedAtRef.current != null) {
      const el = (performance.now() - startedAtRef.current) / 1000;
      elapsedRef.current = el;
      setElapsedSec(el);
    }
    setRunning(false);
    setHourglassOpen(false);
  };
  const onReset = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setRunning(false);
    setHourglassOpen(false);
    elapsedRef.current = 0;
    setElapsedSec(0);
    startedAtRef.current = null;
  };

  useEffect(() => {
    if (running) return;
    setElapsedSec(0);
    startedAtRef.current = null;
    // Intentionally does NOT depend on `running` — we only want to reset when
    // the user changes any of the three selects, not when they Stop the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classNo, subject, marks]);

  const overTime =
    idealSec != null && !running && elapsedSec > 0 && elapsedSec > idealSec;
  const onTime =
    idealSec != null && !running && elapsedSec > 0 && elapsedSec <= idealSec;

  // Circular progress arc (for live elapsed vs ideal)
  const progressPct = idealSec != null ? Math.min(1, elapsedSec / idealSec) : 0;
  const R = 34;
  const CIRC = 2 * Math.PI * R;
  const dashOffset = CIRC * (1 - progressPct);

  const ringColor = overTime
    ? "stroke-rose-500"
    : running || onTime
    ? "stroke-indigo-500"
    : "stroke-indigo-300";

  return (
    <motion.section
      data-testid="time-master-card"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.24, type: "spring", stiffness: 160, damping: 18 }}
      className="relative overflow-hidden mt-4 rounded-[28px] p-5 border-4 border-white
        bg-gradient-to-br from-indigo-100 via-violet-50 to-fuchsia-100
        shadow-[0_10px_0_-2px_rgba(0,0,0,0.06),0_20px_40px_-14px_rgba(15,23,42,0.18)]"
    >
      {/* --- Layered ornaments (match ModuleCard) --- */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/60 via-white/10 to-transparent"
      />
      <svg
        aria-hidden
        className="pointer-events-none absolute -bottom-4 -right-2 w-40 h-40 opacity-[0.22] mix-blend-multiply"
        viewBox="0 0 100 100"
      >
        <defs>
          <pattern id="tm-dots" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="1.5" cy="1.5" r="1.2" fill="#818cf8" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#tm-dots)" />
      </svg>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-6 -left-6 w-40 h-40 rounded-full bg-white/45 blur-3xl"
      />

      {/* --- Header (click to collapse/expand) --- */}
      <button
        type="button"
        data-testid="time-master-header-btn"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="relative w-full flex items-center gap-3 text-left"
      >
        <div className="relative shrink-0">
          <div
            className="w-14 h-14 rounded-2xl bg-white/95 backdrop-blur ring-1 ring-white/70 flex items-center justify-center
              shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_10px_20px_-6px_rgba(99,102,241,0.35)]"
          >
            <motion.div
              style={{ display: "inline-flex", transformOrigin: "50% 55%" }}
              animate={{ rotate: [-6, 6, -6, 6, -6] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            >
              <Clock size={26} strokeWidth={2.5} className="text-indigo-600" />
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.55 }}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-[0_2px_4px_rgba(0,0,0,0.15)]"
          >
            <Sparkles size={11} strokeWidth={3} className="text-indigo-500" />
          </motion.div>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-xl font-bold text-slate-900 leading-tight tracking-tight">
            {t(lang, "time_master")}
          </h3>
          <p className="text-[12px] text-slate-700/85 font-semibold mt-0.5 leading-snug line-clamp-2">
            {t(lang, "time_master_desc")}
          </p>
        </div>
        <div
          aria-hidden
          className="shrink-0 w-9 h-9 rounded-full bg-white/95 border-2 border-white ring-1 ring-slate-900/5 flex items-center justify-center
            shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_4px_10px_-4px_rgba(15,23,42,0.15)]"
        >
          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="text-indigo-600"
          >
            <ChevronDown size={18} strokeWidth={2.75} />
          </motion.span>
        </div>
      </button>

      {/* --- Collapsible body --- */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="tm-body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden"
          >
            <div className="pt-4">
              {/* --- Field selects --- */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <FieldSelect
                  label={t(lang, "time_master_class")}
                  value={classNo}
                  onChange={(e) => setClassNo(e.target.value)}
                  testId="time-master-class"
                >
                  <option value="">{t(lang, "time_master_pick_class")}</option>
                  {CLASSES.map((c) => (<option key={c} value={c}>{c}</option>))}
                </FieldSelect>

                <FieldSelect
                  label={t(lang, "time_master_subject")}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  testId="time-master-subject"
                >
                  <option value="">{t(lang, "time_master_pick_subject")}</option>
                  {SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>{t(lang, s.labelKey)}</option>
                  ))}
                </FieldSelect>

                <FieldSelect
                  label={t(lang, "time_master_marks")}
                  value={marks}
                  onChange={(e) => setMarks(e.target.value)}
                  testId="time-master-marks"
                >
                  <option value="">{t(lang, "time_master_pick_marks")}</option>
                  {MARKS.map((m) => (<option key={m} value={m}>{m}</option>))}
                </FieldSelect>
              </div>

              {/* --- Ideal / Elapsed panel (existing block) --- */}
      <AnimatePresence>
        {allChosen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28 }}
            className="relative overflow-hidden"
          >
            <div className="mt-4 flex items-center gap-3 bg-white/85 backdrop-blur rounded-3xl p-3 ring-1 ring-slate-900/5
                shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_6px_16px_-6px_rgba(15,23,42,0.18)]">
              {/* Circular arc — ideal + progress */}
              <div className="relative shrink-0 w-[92px] h-[92px]">
                <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
                  <circle cx="40" cy="40" r={R} className="stroke-indigo-100" strokeWidth="6" fill="none" />
                  <circle
                    cx="40"
                    cy="40"
                    r={R}
                    className={ringColor}
                    strokeWidth="6"
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={CIRC}
                    strokeDashoffset={dashOffset}
                    style={{ transition: running ? "none" : "stroke-dashoffset 400ms ease" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Target size={14} strokeWidth={2.75} className="text-indigo-500" />
                  <div
                    data-testid="time-master-ideal"
                    className="font-heading text-[15px] font-bold text-indigo-700 tabular-nums leading-none mt-0.5"
                  >
                    {fmtMMSS(idealSec)}
                  </div>
                  <div className="text-[9px] font-heading font-bold uppercase tracking-wider text-slate-500 leading-none mt-1">
                    {t(lang, "time_master_ideal")}
                  </div>
                </div>
              </div>

              {/* Elapsed panel */}
              <div className="flex-1 min-w-0">
                <div
                  className={`rounded-2xl p-3 border-2 text-center transition-colors ${
                    overTime ? "bg-rose-50 border-rose-200"
                    : onTime ? "bg-emerald-50 border-emerald-200"
                    : "bg-indigo-50 border-indigo-100"
                  }`}
                >
                  <div className="flex items-center justify-center gap-1.5 text-[10px] font-heading font-bold uppercase tracking-[0.14em] text-slate-500">
                    <Timer size={11} strokeWidth={3} />
                    <span>{t(lang, "time_master_elapsed")}</span>
                  </div>
                  <div
                    data-testid="time-master-elapsed"
                    className={`font-heading text-3xl sm:text-[34px] font-bold tabular-nums leading-none mt-1 ${
                      overTime ? "text-rose-600"
                      : onTime ? "text-emerald-600"
                      : running ? "text-indigo-600"
                      : "text-slate-800"
                    }`}
                  >
                    {fmtMMSS(elapsedSec)}
                  </div>
                </div>
              </div>
            </div>

            {/* --- Buttons --- */}
            <div className="mt-3 flex gap-2">
              {!running ? (
                <button
                  data-testid="time-master-start-btn"
                  onClick={onStart}
                  className="flex-1 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-500 hover:to-emerald-700 text-white font-heading text-base font-bold uppercase tracking-tight py-3 rounded-full inline-flex items-center justify-center gap-2 border-2 border-white/70 shadow-[0_6px_0_-1px_rgba(0,0,0,0.12),0_10px_18px_-6px_rgba(16,185,129,0.55)] active:translate-y-0.5 active:shadow-none transition-all"
                >
                  <Play size={18} strokeWidth={2.75} />
                  <span>{t(lang, "time_master_start")}</span>
                </button>
              ) : (
                <>
                  <button
                    data-testid="time-master-stop-btn"
                    onClick={onStop}
                    className="flex-1 bg-gradient-to-br from-rose-500 to-rose-600 hover:from-rose-500 hover:to-rose-700 text-white font-heading text-base font-bold uppercase tracking-tight py-3 rounded-full inline-flex items-center justify-center gap-2 border-2 border-white/70 shadow-[0_6px_0_-1px_rgba(0,0,0,0.12),0_10px_18px_-6px_rgba(244,63,94,0.55)] active:translate-y-0.5 active:shadow-none transition-all"
                  >
                    <Square size={18} strokeWidth={2.75} />
                    <span>{t(lang, "time_master_stop")}</span>
                  </button>
                  <button
                    data-testid="time-master-hourglass-btn"
                    onClick={() => setHourglassOpen(true)}
                    aria-label={t(lang, "hourglass_view")}
                    title={t(lang, "hourglass_view")}
                    className="shrink-0 bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-400 hover:to-amber-700 text-white font-heading font-bold uppercase tracking-tight w-12 rounded-full inline-flex items-center justify-center border-2 border-white/70 shadow-[0_6px_0_-1px_rgba(0,0,0,0.12),0_10px_18px_-6px_rgba(217,119,6,0.55)] active:translate-y-0.5 active:shadow-none transition-all"
                  >
                    <Hourglass size={18} strokeWidth={2.75} />
                  </button>
                </>
              )}
              {!running && elapsedSec > 0 && (
                <button
                  data-testid="time-master-reset-btn"
                  onClick={onReset}
                  aria-label="reset"
                  className="bg-white border-2 border-white ring-1 ring-slate-900/10 text-slate-700 font-heading font-bold uppercase tracking-tight text-xs w-12 rounded-full inline-flex items-center justify-center shadow-[0_4px_0_-1px_rgba(0,0,0,0.08)] active:translate-y-0.5 active:shadow-none transition-all"
                >
                  <RotateCcw size={16} strokeWidth={2.75} />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hourglass fullscreen visualization (optional; never owns the timer) */}
      <HourglassView
        open={hourglassOpen}
        onClose={() => setHourglassOpen(false)}
        elapsedRef={elapsedRef}
        idealSec={idealSec}
        running={running}
      />
    </motion.section>
  );
}
